import { HoverCard, Text } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Database, Zap } from "lucide-react";
import type { CSSProperties } from "react";

import {
  getGetTeamStorageQueryKey,
  useGetTeamStorage,
} from "../../api/generated/endpoints/assets/assets";
import { useGetTeamBalance } from "../../api/generated/endpoints/billing/billing";
import type { TeamStorage } from "../../api/generated/model";
import { formatCount, formatStorageGb } from "../../ui/format";
import { GradientIcon } from "../../ui/GradientIcon";
import classes from "./shell.module.css";

/** A bar of the hover card, `share` of it filled (0 to 1), named for a screen reader. */
function UsageBar({ label, share }: { label: string; share: number }) {
  // To a hundredth of a percent: a bar 1,024 px wide shows no finer step.
  const percent = Math.round(Math.min(Math.max(share, 0), 1) * 10_000) / 100;
  const style: CSSProperties & { "--cai-usage-filled": string } = {
    "--cai-usage-filled": `${percent}%`,
  };
  return (
    <progress className={classes.usageBar} style={style} value={percent} max={100} aria-label={label} />
  );
}

/**
 * The team's usage, as live Created.ai's pill (design-system spec 2.2, spec
 * web/usage-pill-storage). Its credits: what is left to spend, and on hover what running jobs
 * hold. Below zero while a charge past the team's credits is owed, as live's balance goes
 * (B1-ledger-core): the negative number shows, and the hover says the next credits pay it first.
 * Then its storage, once read: the GB its files hold, and on hover the GB of its limit over a
 * bar. Nothing shows until the balance has loaded, as live shows no pill without usage. Stream
 * events refetch the balance (api/event-handlers.ts); the team's writes, the window's focus and
 * the hover card's opening read the storage again (api/team-storage.ts).
 */
export function UsagePill({ teamId }: { teamId: string }) {
  const queryClient = useQueryClient();
  const balance = useGetTeamBalance(teamId).data;
  const stored = useGetTeamStorage(teamId, { query: { refetchOnWindowFocus: true } }).data;
  if (balance === undefined) return null;
  const { available, held } = balance;
  const total = available + held;
  const left = Math.max(available, 0);
  const gb = stored === undefined ? undefined : formatStorageGb(stored.used_bytes);
  const label = `${formatCount(available)} credits${gb === undefined ? "" : `, ${gb} GB used`}`;
  return (
    <HoverCard
      width={320}
      position="bottom"
      shadow="md"
      role="tooltip"
      events={{ hover: true, focus: true, touch: true }}
      // A stale read is read again as the card opens: another tab, a member or a save's copy may
      // have changed it.
      onOpen={() => {
        void queryClient.refetchQueries({ queryKey: getGetTeamStorageQueryKey(teamId), stale: true });
      }}
    >
      <HoverCard.Target>
        <button type="button" className={classes.usage} aria-label={label}>
          <span className={classes.usageMetric}>
            <GradientIcon icon={Zap} size={17} filled />
            <Text component="span" size="sm" fw={500} c="var(--cai-color-text)">
              {formatCount(available)}
            </Text>
          </span>
          {gb === undefined ? null : (
            <>
              <span className={classes.usageDivider} />
              <span className={classes.usageMetric}>
                <GradientIcon icon={Database} size={18} />
                <Text component="span" size="sm" fw={500} c="var(--cai-color-text)">
                  {gb}
                </Text>
              </span>
            </>
          )}
        </button>
      </HoverCard.Target>
      <HoverCard.Dropdown className={classes.usageCard}>
        <Text className={classes.usageText}>
          {available < 0
            ? `${formatCount(-available)} credits owed; the next credits added pay them first`
            : `${formatCount(available)} of ${formatCount(total)} credits left`}
          {held > 0 ? `; ${formatCount(held)} held by running jobs` : ""}
        </Text>
        <UsageBar label="Credits left" share={total <= 0 ? 0 : left / total} />
        {stored === undefined ? null : <StorageSection storage={stored} />}
      </HoverCard.Dropdown>
    </HoverCard>
  );
}

/**
 * The hover card's storage section, as live's `getStorageState`: "{used} of {limit} GB used" over
 * a bar of the share used, or "{used} GB used" with no bar while the team has no limit, and none
 * with a limit of 0 (live's plan canceled or past due).
 */
function StorageSection({ storage }: { storage: TeamStorage }) {
  const { used_bytes: used, limit_bytes: limit } = storage;
  if (limit === 0) return null;
  return (
    <>
      <div className={classes.usageSectionDivider} />
      <Text className={classes.usageText}>
        {limit === null
          ? `${formatStorageGb(used)} GB used`
          : `${formatStorageGb(used)} of ${formatStorageGb(limit)} GB used`}
      </Text>
      {limit === null ? null : <UsageBar label="Storage used" share={used / limit} />}
    </>
  );
}
